import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, API_BASE, LimitExceededError, LimitExceededDetail, PlanRequiredError, PlanRequiredDetail } from '../lib/api'
import { supabase } from '../lib/supabase'

// ---------------------------------------------------------------------------
// Tipi
// ---------------------------------------------------------------------------

export interface CostEntry {
  id: string
  tenant_id: string
  business_unit_id: string | null
  cost_type: 'direct' | 'indirect' | 'overhead' | null
  behavior_type: 'fixed' | 'variable' | 'semi_variable' | null
  category: string
  description: string | null
  amount: number
  currency: string
  competence_date: string
  data_source_id: string | null
  external_id: string | null
  created_at: string
  updated_at: string
}

export interface CostEntriesListResponse {
  items: CostEntry[]
  total: number
}

export interface CostEntryCreate {
  category: string
  description?: string
  amount: string
  currency?: string
  competence_date: string
  cost_type?: string
  behavior_type?: string
  business_unit_id?: string
}

export interface CostEntryUpdate {
  category?: string
  description?: string
  amount?: string
  currency?: string
  competence_date?: string
  cost_type?: string
  behavior_type?: string
  business_unit_id?: string
}

export interface CostEntriesFilters {
  year?: number
  category?: string
  cost_type?: string
  bu_id?: string
  limit?: number
  offset?: number
}

export interface ImportResult {
  rows_total: number
  rows_imported: number
  rows_skipped: number
  rows_error: number
  errors: Array<{ row: number; error: string }>
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useCostEntries(filters: CostEntriesFilters = {}) {
  const params = new URLSearchParams()
  if (filters.year) params.set('year', String(filters.year))
  if (filters.category) params.set('category', filters.category)
  if (filters.cost_type) params.set('cost_type', filters.cost_type)
  if (filters.bu_id) params.set('bu_id', filters.bu_id)
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.offset) params.set('offset', String(filters.offset))
  const qs = params.toString() ? `?${params.toString()}` : ''

  return useQuery<CostEntriesListResponse>({
    queryKey: ['cost-entries', filters],
    queryFn: () => api.get<CostEntriesListResponse>(`/api/v1/cost-entries${qs}`),
  })
}

export function useCostEntryCategories() {
  return useQuery<string[]>({
    queryKey: ['cost-entries', 'categories'],
    queryFn: () => api.get<string[]>('/api/v1/cost-entries/categories'),
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateCostEntry() {
  const qc = useQueryClient()
  return useMutation<CostEntry, Error, CostEntryCreate>({
    mutationFn: (body) => api.post<CostEntry>('/api/v1/cost-entries', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cost-entries'] }),
  })
}

export function useUpdateCostEntry() {
  const qc = useQueryClient()
  return useMutation<CostEntry, Error, { id: string; body: CostEntryUpdate }>({
    mutationFn: ({ id, body }) => api.patch<CostEntry>(`/api/v1/cost-entries/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cost-entries'] }),
  })
}

export function useDeleteCostEntry() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete(`/api/v1/cost-entries/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cost-entries'] }),
  })
}

export function useImportCostEntries() {
  const qc = useQueryClient()
  return useMutation<ImportResult, Error, File>({
    mutationFn: async (file) => {
      const form = new FormData()
      form.append('file', file)
      const { data } = await supabase.auth.getSession()
      const token = data.session?.access_token
      const res = await fetch(`${API_BASE}/api/v1/cost-entries/import`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        if (res.status === 402 && body?.detail?.error === 'plan_required') {
          throw new PlanRequiredError(body.detail as PlanRequiredDetail)
        }
        if (res.status === 402 && body?.detail?.error === 'limit_exceeded') {
          throw new LimitExceededError(body.detail as LimitExceededDetail)
        }
        throw new Error(body?.detail ?? `Import fallito: ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cost-entries'] }),
  })
}
